import React from 'react';
import PropTypes from 'prop-types';
import { LuSparkles } from 'react-icons/lu';
import logo from '../../../assets/logo.png';
import { GLASS_CHIP } from '../../../components/ui/glass';
import { getGreetingKey } from '../../../utils/greeting';
import { useTranslation } from '../../../i18n/LanguageContext';

const DATE_FORMAT = { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' };

/** Greeting card at the top of the dashboard. */
const DashboardHero = ({ userName }) => {
  const { t, locale } = useTranslation();
  const now = new Date();

  return (
    <section className="tw-relative tw-isolate tw-overflow-hidden tw-rounded-[2rem] tw-border tw-border-white/80 tw-bg-white/70 tw-p-6 tw-shadow-[inset_0_1px_0_rgba(255,255,255,1),0_20px_50px_-30px_rgba(15,23,42,0.35)] sm:tw-p-8 lg:tw-px-10 lg:tw-py-9 dark:tw-border-white/10 dark:tw-bg-[#22262f]/80 dark:tw-shadow-[inset_0_1px_0_rgba(255,255,255,0.07),0_20px_50px_-30px_rgba(0,0,0,0.9)]">
      {/* Soft aurora behind the content */}
      <div aria-hidden="true" className="tw-pointer-events-none tw-absolute -tw-left-24 -tw-top-28 -tw-z-10 tw-h-72 tw-w-72 tw-rounded-full tw-bg-teal-300/50 tw-blur-3xl dark:tw-bg-teal-500/20" />
      <div aria-hidden="true" className="tw-pointer-events-none tw-absolute -tw-right-16 -tw-top-20 -tw-z-10 tw-h-64 tw-w-64 tw-rounded-full tw-bg-violet-300/40 tw-blur-3xl dark:tw-bg-violet-600/20" />
      <div aria-hidden="true" className="tw-pointer-events-none tw-absolute -tw-bottom-32 tw-left-1/3 -tw-z-10 tw-h-56 tw-w-56 tw-rounded-full tw-bg-sky-300/30 tw-blur-3xl dark:tw-bg-sky-500/15" />

      <div className="tw-flex tw-flex-col tw-gap-6 lg:tw-flex-row lg:tw-items-center lg:tw-justify-between">
        <div className="tw-min-w-0 tw-max-w-2xl">
          <p className={`tw-inline-flex tw-items-center tw-gap-2 tw-rounded-full tw-px-3 tw-py-1 tw-text-xs tw-font-semibold tw-text-teal-700 dark:tw-text-teal-300 ${GLASS_CHIP}`}>
            <LuSparkles aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />
            {now.toLocaleDateString(locale, DATE_FORMAT)}
          </p>
          <h1 className="tw-mt-4 tw-text-3xl tw-font-semibold tw-leading-tight tw-tracking-tight tw-text-slate-900 sm:tw-text-4xl lg:tw-text-[2.75rem] dark:tw-text-white">
            {t(`common.greeting.${getGreetingKey(now.getHours())}`)}
            {userName && (
              <>
                ,{' '}
                <span className="tw-bg-gradient-to-r tw-from-teal-500 tw-via-cyan-500 tw-to-violet-500 tw-bg-[length:200%_auto] tw-bg-clip-text tw-text-transparent tw-animate-gradient-x motion-reduce:tw-animate-none dark:tw-from-teal-300 dark:tw-via-cyan-300 dark:tw-to-violet-400">
                  {userName}
                </span>
              </>
            )}
          </h1>
          <p className="tw-mt-3 tw-text-sm tw-leading-relaxed tw-text-slate-600 sm:tw-text-base dark:tw-text-slate-400">
            {t('dashboard.subtitle')}
          </p>
        </div>
        <img src={logo} alt="HabcareCRO" className="tw-hidden tw-h-14 tw-w-auto tw-shrink-0 lg:tw-block xl:tw-h-16" />
      </div>
    </section>
  );
};

DashboardHero.propTypes = {
  userName: PropTypes.string,
};

export default DashboardHero;
